/* eslint-disable jsx-a11y/control-has-associated-label */
import React from "react";
import { ATCONSTANTS as ATC } from "appconstants";
import { displayBooleanAsYN } from "helpers";
import {
  FasHistory,
  FasExclamationTriangle,
  FasCheckCircle,
} from "assets/icons";
import { RenderElement } from "components/RenderHelpers";
import {
  CanSeeIndexingGo,
  CanSeeIndexingHistory,
  CanSeeIndexingIRB,
  CanSeeIndexingSR,
  CanSeeIndexingChangePI,
  CanSeeSRPktHistory,
} from "appconstants/Permission";
import SiteIButton from "components/iButton/SiteIButton";
import ProtocolIButton from "components/iButton/ProtocolIButton";
import PersonIButton from "components/iButton/PersonIButton";

const SITE_CTEP_ID = {
  name: "siteCtepId",
  label: "Inst CTEP ID",
  isEditable: true,
  renderCell: (row) => (
    <span>
      <SiteIButton siteCtepId={row.siteCtepId} />
      &nbsp;
      {row.siteCtepId}
    </span>
  ),
};
const IS_SR = {
  name: "isSR",
  label: "SR?",
  isEditable: false,
  type: 'boolean',
  renderCell: (data) => displayBooleanAsYN(data.isSR),
};

const SR_STATUS = {
  name: "srStatus",
  label: "SR Status",
  isEditable: false,
  showWhitespace: true,
};
const SITE_NAME = {
  name: "siteName",
  label: "Inst Name",
  isEditable: false,
  showWhitespace: true,
};
const PROTOCOL_NUMBER = {
  name: "protocolNumber",
  label: "Protocol",
  isEditable: true,
  showWhitespace: true,
  renderCell: (data) => (
    <span title={`CR Required: ${data.isStatusCb ? "Yes" : "No"} `}>
      <ProtocolIButton protocolNumber={data.protocolNumber} />
      <span>{data.protocolNumber}</span>
    </span>
  ),
};
const ATTRIBUTE = {
  name: "objKeyValue",
  label: "Attributes",
  isEditable: true,
};
const DOCUMENT_TYPE = { ...ATTRIBUTE, label: "Document Type" };
const PERSON_CTEP_ID = {
  name: "personCtepId",
  label: "Person CTEP ID",
  isEditable: true,
  showWhitespace: true,
};
const PERSON_NAME = {
  name: "personName",
  label: "Name",
  isEditable: false,
  renderCell: (data) => (
    <>
      {data.personName != null ? (
        <PersonIButton personCtepId={data.personCtepId} />
      ) : null}
      <span className="mr-2">{data.personName}</span>
    </>
  ),
};
const PATIENT_ID = { name: "patientId", label: "Patient ID", isEditable: true };
const GO_ACTION = {
  isAction: true,
  label: "Actions",
  name: "go",
  sort: false,
  isVisible: CanSeeIndexingGo,
  renderCell: (row, events) => {
    const onClick = () => events[0](row);
    return (
      <button
        data-id={`${ATC.PACKETID}${row.id}_PACKET_TYPE_GO${ATC.BUTTON}`}
        type="button"
        className="btn turquoise"
        onClick={onClick}
        title="Go to Rostering Application"
      >
        Go
      </button>
    );
  },
};
const HISTORY_ACTION = {
  isAction: true,
  label: "",
  name: "action",
  sort: false,
  isVisible: CanSeeIndexingHistory,
  renderCell: (row, events) => {
    const onClick = () => events[1](row);
    return (
      <RenderElement access={CanSeeSRPktHistory()}>
        <button
          type="button"
          data-id={`${ATC.PACKETID}${row.id}_PACKET_TYPE_HISTORY${ATC.BUTTON}`}
          className="fn-btn font-lg mr-1"
          title="Packet History"
          onClick={onClick}
        >
          <FasHistory />
        </button>
      </RenderElement>
    );
  },
};

function ButtonField({ name, row, isDisabled, onClick, icon, tooltip }) {
  return (
    <span className="mx-1 align-self-center">
      <button
        data-id={`${ATC.PACKETID}${row.id}_${name
          .split(" ")
          .join("_")
          .toUpperCase()}${ATC.BUTTON}`}
        className={`btn turquoise not-padded pr-2 ${icon ? "pl-1" : "pl-2"}`}
        type="button"
        title={tooltip || name}
        disabled={isDisabled}
        onClick={onClick}
      >
        {icon}
        {name}
      </button>
    </span>
  );
}

const RenderIrbOrLpo = ({ disabled, tooltip, ...props }) => {
  const { row, event } = props;
  const label = row.isIrbExempt ? "LPO" : "IRB";
  const existTooltip = `Open ${label} Approvals in new tab | ${label}${
    row.irbCount ? " is Entered" : " is Not Entered"
  }`;
  return (
    <RenderElement access={CanSeeIndexingIRB()}>
      <ButtonField
        {...props}
        name={label}
        isDisabled={disabled}
        onClick={() => event[3](row)}
        tooltip={tooltip || existTooltip}
        icon={
          <i>
            {row.irbCount ? <FasCheckCircle /> : <FasExclamationTriangle />}
          </i>
        }
      />
    </RenderElement>
  );
};

function ActionButtons(props) {
  const { row, event } = props;
  const isDisabled = !row.isSR;
  const isDisableLpo = row.exemptPSRCount === 0;

  let type = "IRB/IRBA";
  if (row.isIrbExempt) {
    type = "LPO Approval"
  }

  return (
    <>
      {!row.isIrbExempt ? (
        <RenderIrbOrLpo disabled={isDisabled} {...props} />
      ) : (
        <RenderElement access={row.isSR}>
          <RenderIrbOrLpo
            disabled={isDisableLpo}
            tooltip={
              isDisableLpo
                ? "There is no LPO Approval PSR for this protocol."
                : null
            }
            {...props}
          />
        </RenderElement>
      )}
      <RenderElement access={CanSeeIndexingSR()}>
        <ButtonField
          {...props}
          name="SR"
          isDisabled={isDisabled}
          onClick={() => event[2](row)}
          tooltip={`Open Site Registration in new tab | ${type} ${
            row.srCount ? "is complied" : "is not complied"
          }`}
          icon={
            <i>
              {row.srCount ? <FasCheckCircle /> : <FasExclamationTriangle />}
            </i>
          }
        />
      </RenderElement>
      <RenderElement access={CanSeeIndexingChangePI()}>
        <ButtonField
          {...props}
          name="Chg PI"
          isDisabled={isDisabled || !row.latestIrbId}
          onClick={() => event[4](row)}
          tooltip="Change PI"
        />
      </RenderElement>
    </>
  );
}

const ACTIONS = {
  isAction: true,
  name: "actions",
  label: "Actions",
  sort: false,
  renderCell: (row, events) => <ActionButtons row={row} event={events} />,
};

const COLUMNS_PACKET_TYPE_MAP = {
  SITE_REGISTRATION: [
    SITE_CTEP_ID,
    SITE_NAME,
    PROTOCOL_NUMBER,
    IS_SR,
    SR_STATUS,
    ACTIONS,
    HISTORY_ACTION,
  ],
  INSTITUTION: [SITE_CTEP_ID, SITE_NAME, ATTRIBUTE, GO_ACTION],
  PERSON: [PERSON_CTEP_ID, PERSON_NAME, ATTRIBUTE, GO_ACTION],
  MEMBERSHIP: [SITE_CTEP_ID, SITE_NAME, DOCUMENT_TYPE],
  ENROLLMENT_TRANSFERS: [PATIENT_ID, { ...PROTOCOL_NUMBER, isEditable: false }],
  PAPER_CRFS_QUERIES: [
    SITE_CTEP_ID,
    SITE_NAME,
    PROTOCOL_NUMBER,
    HISTORY_ACTION,
  ],
};

const columnsType = (type) => COLUMNS_PACKET_TYPE_MAP[type] || [];
export default columnsType;
